/**
 * SectionRail + SectionHeaderBar — the Events tab's layout, as parts (2026-10-05).
 *
 * The Events list draws a white header bar (title, one sentence, actions right) over a
 * 236px sectioned left rail (VIEWS / FOLDERS). Edit Event uses the same rail for its Setup
 * steps. Customers and the guest list adopt it here, so the metrics live in ONE place:
 * row / count / heading styles are collections-ui's own (railRow, RailCount, RailHeading).
 *
 * Every row is a real control — it filters, navigates or selects; the rail never draws a
 * row that does nothing.
 */
import React, { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { railRow, RailCount, RailHeading } from "./collections-ui";

export interface SectionRailRow {
  id: string;
  label: string;
  count?: number | null;
  /** A readiness/status dot in place of a count (Edit Event's steps). */
  dot?: boolean | null;
  active?: boolean;
  onClick: () => void;
  /** Leading glyph for action rows ("+ New segment"). */
  lead?: ReactNode;
  testid?: string;
}
export interface SectionRailGroup { id: string; heading: string; rows: SectionRailRow[]; role?: "tablist" }

export function SectionRail({ groups, ariaLabel, footer }: { groups: SectionRailGroup[]; ariaLabel: string; footer?: ReactNode }) {
  return (
    <nav aria-label={ariaLabel} style={{ flex: "0 0 236px", width: 236, minWidth: 236, overflowY: "auto", background: T.white, borderRight: `1px solid ${T.n5}`, padding: "16px 13px 20px", display: "flex", flexDirection: "column", gap: 6, boxSizing: "border-box" }}>
      {groups.filter((g) => g.rows.length).map((g, gi) => (
        <React.Fragment key={g.id}>
          <RailHeading first={gi === 0}>{g.heading}</RailHeading>
          <div role={g.role} aria-orientation={g.role ? "vertical" : undefined} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {g.rows.map((r) => (
              <button key={r.id} type="button" role={g.role ? "tab" : undefined} aria-selected={g.role ? !!r.active : undefined} aria-current={!g.role && r.active ? "true" : undefined}
                onClick={r.onClick} data-testid={r.testid} style={{ ...railRow(!!r.active), ...(r.lead ? { color: T.p1 } : {}) }}>
                {r.lead ? <span style={{ display: "inline-flex", flex: "none" }}>{r.lead}</span> : null}
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</span>
                {r.dot === true || r.dot === false ? <span style={{ width: 6, height: 6, borderRadius: 999, background: r.dot ? "#1F8A5B" : "#C0392B", flex: "none" }} /> : <RailCount n={r.count} active={!!r.active} />}
              </button>
            ))}
          </div>
        </React.Fragment>
      ))}
      {footer ? <><span style={{ flex: 1 }} />{footer}</> : null}
    </nav>
  );
}

/** The Events list header bar: white, full width, title + one sentence left, actions right. */
export function SectionHeaderBar({ title, sub, actions, lead }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; lead?: ReactNode }) {
  return (
    <header style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 14, padding: "10px 16px", background: T.white, borderBottom: `1px solid ${T.n5}`, boxShadow: "0 1px 2px rgba(51,51,51,0.06)", zIndex: 5, flexWrap: "wrap" }}>
      {lead ? <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 10 }}>{lead}</div> : null}
      <div style={{ flex: "1 1 240px", minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
        {sub ? <div style={{ fontSize: 12.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div> : null}
      </div>
      {actions ? <div style={{ flex: "0 1 auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>{actions}</div> : null}
    </header>
  );
}

/** Header-bar back control — the boxed ← Edit Event uses. */
export function BackBox({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick}
      style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: `1px solid ${T.n5}`, background: T.white, color: T.n1, cursor: "pointer", fontSize: 15, fontFamily: T.font }}>←</button>
  );
}

/** The width a list actually has (2026-10-05): measured after each render and on window resize,
 *  written only on a real change — no ResizeObserver (one froze Edit Event). Lists use it to
 *  drop low-priority columns so the page never scrolls sideways. */
export function useElementWidth<E extends HTMLElement = HTMLDivElement>(): [(el: E | null) => void, number] {
  const el = useRef<E | null>(null);
  const [w, setW] = useState(0);
  const measure = useCallback(() => { const n = el.current ? Math.floor(el.current.clientWidth) : 0; setW((p) => (Math.abs(p - n) < 1 ? p : n)); }, []);
  const ref = useCallback((node: E | null) => { el.current = node; if (node) window.requestAnimationFrame(measure); }, [measure]);
  useEffect(() => { measure(); });
  useEffect(() => { window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }, [measure]);
  return [ref, w];
}
